/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');

class ImprovedTabularRecordSetProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
		this.options = pOptions;
		this.renderOverriden = false;
	}

	prepareTable()
	{
		const activeRecordSet = this.pict.providers?.RecordSetRouter?.pictRouter?.router?.current?.[0]?.data?.RecordSet || '';
		const isDocumentRecord = activeRecordSet === 'Document';
		// override table display logic
		if (!this.renderOverriden)
		{
			const beforeRenderStubList = this.pict.views['PRSP-List-RecordList'].onBeforeRender.bind(this.pict.views['PRSP-List-RecordList']); ;
			this.pict.views['PRSP-List-RecordList'].onBeforeRender =
			function(callback, renderable)
			{
				this.pict.providers.ImprovedTabularRecordSetProvider.prepareTable();
				beforeRenderStubList(callback, renderable);
			};
			const beforeRenderStubDashboard = this.pict.views['PRSP-Dashboard-RecordList'].onBeforeRender.bind(this.pict.views['PRSP-Dashboard-RecordList']); ;
			this.pict.views['PRSP-Dashboard-RecordList'].onBeforeRender =
			function(callback, renderable)
			{
				this.pict.providers.ImprovedTabularRecordSetProvider.prepareTable();
				beforeRenderStubDashboard(callback, renderable);
			};
			// Permission-expression layer (Phase 2b): gate the record read-view Edit/Save buttons by
			// can(entity,'Update'). The read view shows the Edit button via getElementById after render
			// (RecordSet-Read.js), so removing it from the DOM would null-crash those calls — instead a body
			// class + !important CSS (below) hides it while it stays in the DOM. renderRead is the definitive
			// per-record-page method; wrapping it sets the body class from the active route each time.
			const tmpReadView = this.pict.views['RSP-RecordSet-Read'];
			if (tmpReadView && typeof tmpReadView.renderRead === 'function' && !tmpReadView._pcfgEditGateInstalled)
			{
				const tmpOriginalRenderRead = tmpReadView.renderRead.bind(tmpReadView);
				tmpReadView.renderRead = async function(...pRenderArgs)
				{
					const tmpRenderResult = await tmpOriginalRenderRead(...pRenderArgs);
					try { _Pict.providers.ImprovedTabularRecordSetProvider.applyReadEditGate(); }
					catch (pGateError) { /* non-fatal: gating is a UI hint, the server stays authoritative */ }
					return tmpRenderResult;
				};
				tmpReadView._pcfgEditGateInstalled = true;
			}
			this.renderOverriden = true;
			if (this.pict.CSSMap && typeof this.pict.CSSMap.addCSS === 'function')
			{
				// Neutral list "New" button (see PRSP-List-Title-Template below); brand-coloured on hover/focus.
				this.pict.CSSMap.addCSS('ImprovedTabularRecordSet-ListNew-CSS', /*css*/`
					.button.prsp-list-new { background: transparent; border-color: var(--theme-color-border-default, #d7dce3); color: var(--theme-color-text-muted, #6b7686); transition: color 0.12s ease, border-color 0.12s ease; }
					.button.prsp-list-new:hover, .button.prsp-list-new:focus { background: transparent; border-color: var(--theme-color-brand-primary, #156dd1); color: var(--theme-color-brand-primary, #156dd1); box-shadow: none; }
					.hl-view-description { margin: 0.25rem 0 0.75rem; color: var(--theme-color-text-muted, #6b7686); font-size: 0.875rem; }
					.hl-view-description:empty { display: none; }
					th.prsp-sortable-header { cursor: pointer; user-select: none; white-space: nowrap; }
					th.prsp-sortable-header:hover { color: var(--theme-color-brand-primary, #156dd1); }
					th.prsp-sortable-header .prsp-sort-indicator { margin-left: 0.25em; }
					/* Permission-expression layer: hide the record Edit/Save buttons when the current user can't update the active entity. */
					body.pcfg-cannot-update-record #PRSP-Read-EditButton, body.pcfg-cannot-update-record #PRSP-Read-SaveButton { display: none !important; }
				`, 500);
				this.pict.CSSMap.injectCSS();
			}
		}
		if (this.options?.overrideURLTemplate)
		{
			this.pict.TemplateProvider.addTemplate('PRSP-Read-Link-URL-Template', this.options.overrideURLTemplate);
		}
		// List "New" action. createFromActiveList() reads the live #/PSRS/<RecordSet>/List hash at CLICK time and
		// routes to that record-set's Create form, so it is always correct regardless of render/route ordering
		// (baking the record-set into the template at registration time races with the title render). Rendered
		// here, not via an app-level override, so it survives prepareTable()'s per-render re-registration.
		// Neutral .prsp-list-new style, brand-coloured on hover.
		// Permission-expression layer (Phase 2): the "New" button renders only where the current user can
		// create the entity. Gated by a template tag on the record set's OWN config flag
		// (RecordSetListShowCreateButton, stamped per can(entity,'Create') at login) — not a JS-time check on
		// the active route, which the title can out-race (the same reason createFromActiveList reads the hash
		// at click time). Record.RecordSetConfiguration is always the correct entity's config at title render.
		this.pict.TemplateProvider.addTemplate('HLPC-List-New-Button', /*html*/`<div class="column is-narrow"><button class="button is-small prsp-list-new" title="Create a new record" onclick="_Pict.providers.ImprovedTabularRecordSetProvider.createFromActiveList()"><span class="icon is-small"><i class="fas fa-plus"></i></span><span>New</span></button></div>`);
		const tmpNewButton = `{~TIfAbs:HLPC-List-New-Button:Record:Record.RecordSetConfiguration.RecordSetListShowCreateButton^TRUE^~}`;
		this.pict.TemplateProvider.addTemplate('PRSP-List-Title-Template', /*html*/`
			<!--START ImprovedTabularRecordSet Custom: [PRSP-List-Title-Template] -->
				<header id="PRSP_Title_Container" class="columns is-mobile is-vcentered">
					<div class="column"><h2 class="title is-size-4 mb-0" id="PRSP_Title">{~D:Record.Title~}</h2></div>
					${ this.options?.endCapTemplate || '' }
					<div class="is-hidden-mobile column is-narrow pt-3 has-text-text is-size-7 has-text-right">{~T:PRSP-List-Pagination-Template-Description~}</div>
					${ tmpNewButton }
				</header>
				<div class="is-hidden-desktop is-hidden-tablet pb-2 has-text-text is-size-7 has-text-centered">{~T:PRSP-List-Pagination-Template-Description~}</div>
				<p class="hl-view-description">{~D:Record.RecordSetConfiguration.RecordSetHeaderDescription~}</p>
			<!--  END ImprovedTabularRecordSet Custom: [PRSP-List-Title-Template] -->
		`);

		// Trim the verbose "… total records." tail from the shared pagination description (used by the title bar
		// and the top/bottom pagination). Keeps the live-updated count spans the pagination logic writes to.
		this.pict.TemplateProvider.addTemplate('PRSP-List-Pagination-Template-Description', /*html*/`
			<div>
				Showing
				<span id="PRSP_Pagination_Description_Records_Start">{~D:Record.PageStart~}</span> to
				<span id="PRSP_Pagination_Description_Records_End">{~D:Record.PageEnd~}</span> of
				<span id="PRSP_Pagination_Description_Records_Total">{~D:Record.TotalRecordCount.Count~}</span>
			</div>
		`);
		this.pict.TemplateProvider.addTemplate('PRSP-List-RecordList-Template-Table', /*html*/`
			<!-- START ImprovedTabularRecordSet Custom: [PRSP-List-RecordList-Template-Table] -->
			<div class="table-container">
				<table id="PRSP_List_Table" class="table is-striped is-hoverable is-fullwidth is-bordered">
					<thead>{~T:PRSP-List-RecordListHeader-Template~}</thead>
					<tbody id="PRSP_RecordList_Container_Entries">
						{~T:PRSP-List-RecordListEntry-Template~}
						{~T:PRSP-List-RecordList-Template-EmptyTable~}
					</tbody>
				</table>
			</div>
			<!-- END ImprovedTabularRecordSet Custom: [PRSP-List-RecordList-Template-Table] -->
		`);
		this.pict.TemplateProvider.addTemplate('PRSP-List-RecordList-Template-EmptyTable', /*html*/`
			<!-- START ImprovedTabularRecordSet Custom: [PRSP-List-RecordList-Template-EmptyTable] -->
			<tr id="PRSP_List_Table_Empty">
				<td colspan="100%" class="has-text-centered has-text-text-50">
					<span class="icon">
						<i class="fas fa-info-circle"></i>
					</span>
					<span class="icon-text">No records found.</span>
				</td>
			</tr>
			<!-- END ImprovedTabularRecordSet Custom: [PRSP-List-RecordList-Template-EmptyTable] -->
		`);
		// Row "Print" action (QR label, or the file for a Document row). On for every list when the provider is
		// registered with includePrint: true; otherwise per record set, through the RecordSetListShowPrintButton
		// flag on the record set's own config (LIMS turns it on for Sample and Location). TIfAbs TRUE is a strict
		// `=== true` test, so the flag must be a real boolean.
		this.pict.TemplateProvider.addTemplate('HLPC-List-Print-Button', /*html*/`
						<li>
							<a class="button is-ghost" href="javascript:void(0)" onclick="event.preventDefault();
									if (window.location.href.includes('Document/List'))
									{
										_Pict.providers.DocumentProvider.viewDocumentFile('_blank', false, {~D:Record.Data.IDDocument~});
									}
									else
									{
										_Pict.providers.QRCodeProvider.printDecoratedEntity('{~D:Record.Payload.RecordSetConfiguration.Entity~}', '{~D:Record.Data.QRCode~}');
									}">
								<span class="icon">
									<i class="fas fa-print"></i>
								</span>
								<span class="icon-text">Print</span>
							</a>
						</li>`);
		this.pict.TemplateProvider.addTemplate('HLPC-List-Print-Button-Gate', this.options.includePrint
			? `{~T:HLPC-List-Print-Button~}`
			: `{~TIfAbs:HLPC-List-Print-Button:Record:Record.Payload.RecordSetConfiguration.RecordSetListShowPrintButton^TRUE^~}`);
		this.pict.TemplateProvider.addTemplate('PRSP-List-RecordListAction-Template-Cell', this.options.disableActions ? '' : /*html*/`
			<!-- START ImprovedTabularRecordSet Custom: [PRSP-List-RecordListAction-Template-Cell'] -->
			<td class="has-text-centered prsp-row-actions">
				<ul class="buttons has-addons is-centered">
					{~T:HLPC-List-Print-Button-Gate~}
					{~TSWP:PRSP-List-RecordListAction-Template-Cell-Datum:Pict.providers.RecordSetLinkManager.linkTemplates:Record~}
					${ isDocumentRecord ? /*html*/`<li>
						<a class="button is-ghost" onclick="_Pict.providers.DocumentProvider.openFormByIDDocument(event, {~D:Record.Data.IDDocument~});" title="{~TBR:Record.Name~}">
							<span class="icon">
								<i class="fas fa-pencil"></i>
							</span>
							<span class="icon-text">Edit</span>
						</a>
					</li>` : ''}
				</ul>
			</td>
			<!-- END ImprovedTabularRecordSet Custom:   [PRSP-List-RecordListAction-Template-Cell] -->`);
		this.pict.TemplateProvider.addTemplate('PRSP-List-RecordListAction-Template-Cell-Datum', /*html*/`
			<!-- START ImprovedTabularRecordSet Custom: [PRSP-List-RecordListAction-Template-Cell-Datum] -->
			<li>
				<a class="button is-ghost" {~NE:Record.Data.Default^data-prsp-default="1"~} href="{~TBR:Record.Data.URL~}" title="{~TBR:Record.Data.Name~}">
					<span class="icon">
						<i class="fas fa-search"></i>
					</span>
					<span class="icon-text">View</span>
				</a>
			</li>
			<!-- END ImprovedTabularRecordSet Custom: [PRSP-List-RecordListAction-Template-Cell-Datum] -->
		`);
		// Keep pict-section-recordset's per-column dispatch so record sets that opt into RecordSetListSortable get clickable headers.
		this.pict.TemplateProvider.addTemplate('PRSP-List-RecordListHeader-Template-Header', /*html*/`{~TS:PRSP-List-RecordListHeader-Header-Sortable:Record.SortableSlot~}{~TS:PRSP-List-RecordListHeader-Header-Plain:Record.PlainSlot~}`);
		this.pict.TemplateProvider.addTemplate('PRSP-List-RecordListHeader-Header-Plain', /*html*/`<th>{~D:Record.DisplayName~}</th>`);
		this.pict.TemplateProvider.addTemplate('PRSP-List-RecordListHeader-Header-Sortable', /*html*/`<th class="prsp-sortable-header" title="Sort by {~D:Record.DisplayName~}" onclick="_Pict.views['RSP-RecordSet-List'].sortByColumn('{~D:Record.RecordSet~}', '{~D:Record.Key~}')">{~D:Record.DisplayName~}<span class="icon is-small prsp-sort-indicator">{~TS:PRSP-List-RecordListHeader-Header-SortAscending:Record.AscendingSlot~}{~TS:PRSP-List-RecordListHeader-Header-SortDescending:Record.DescendingSlot~}</span></th>`);
		this.pict.TemplateProvider.addTemplate('PRSP-List-RecordListHeader-Header-SortAscending', /*html*/`<i class="fas fa-sort-up"></i>`);
		this.pict.TemplateProvider.addTemplate('PRSP-List-RecordListHeader-Header-SortDescending', /*html*/`<i class="fas fa-sort-down"></i>`);
		this.pict.TemplateProvider.addTemplate('PRSP-List-RecordListActions-Template-Header', this.options.disableActions ? '' : /*html*/`<th class="has-text-centered">Actions</th>`);
		this.pict.TemplateProvider.addTemplate('PRSP-List-Pagination-Template-Button-Previous',
			`<a class="pagination-previous" href="{~D:Record.PageLinkBookmarks.PreviousLink.URL~}" aria-label="Previous page">Previous</a>`);
		this.pict.TemplateProvider.addTemplate('PRSP-List-Pagination-Template-Button-Next',
			`<a class="pagination-next" href="{~D:Record.PageLinkBookmarks.NextLink.URL~}" aria-label="Next page">Next</a>`);
		this.pict.TemplateProvider.addTemplate('PRSP-List-PaginationTop-Template', /*html*/`
			<!-- START ImprovedTabularRecordSet Custom: [PRSP-List-PaginationTop-Template] -->
			<nav class="pagination is-small my-3 is-rounded" id="RSP_Upper_Pagination_Container" role="navigation" aria-label="pagination">
				{~TIfAbs:PRSP-List-Pagination-Template-Button-Previous:Record:Record.PageLinkBookmarks.ShowPreviousLink^TRUE^true~}
				{~TIfAbs:PRSP-List-Pagination-Template-Button-Next:Record:Record.PageLinkBookmarks.ShowNextLink^TRUE^true~}
				<ul class="pagination-list">{~TS:PRSP-List-Pagination-Template-Pages:Record.PageLinksLimited~}</ul>
			</nav>
			<!-- END ImprovedTabularRecordSet Custom: [PRSP-List-PaginationTop-Template] -->
		`);
		this.pict.TemplateProvider.addTemplate('PRSP-List-Pagination-Template-Pages', /*html*/`
			<!-- START ImprovedTabularRecordSet Custom: [PRSP-List-Pagination-Template-Pages] -->
			<li><a class="pagination-link page-offset_{~D:Record.RelativeOffset~} {~TIfAbs:PaginationActiveClass:Record:Record.RelativeOffset^==^0~}" href="{~D:Record.URL~}" aria-label="Go to page {~D:Record.Page~}">{~D:Record.Page~}</a></li>
			<!-- END ImprovedTabularRecordSet Custom: [PRSP-List-Pagination-Template-Pages] -->
		`);
		this.pict.TemplateProvider.addTemplate('PaginationActiveClass', /*html*/`is-current`);
		this.pict.TemplateProvider.addTemplate('PRSP-List-PaginationBottom-Template', `{~T:PRSP-List-PaginationTop-Template~}`);
		this.pict.TemplateProvider.addTemplate('PRSP-SUBSET-Filters-Template-Button-Fieldset', /*html*/`<!-- intentionally blank -->`);

		// Dashboard templates
		if (this.options?.overrideDashboardURLTemplate)
		{
			this.pict.TemplateProvider.addTemplate('PRSP-Read-Dashboard-URL-Template', this.options.overrideURLTemplate);
		}
		this.pict.TemplateProvider.addTemplate('PRSP-Dashboard-Title-Template', /*html*/`
			<!--START ImprovedTabularRecordSet Custom: [PRSP-Dashboard-Title-Template] -->
				<header id="PRSP_Title_Container" class="columns is-mobile is-vcentered">
					<div class="column"><h2 class="title is-size-4 mb-0" id="PRSP_Title">{~D:Record.Title~}</h2></div>
					${ this.options?.endCapTemplate || '' }
					<div class="is-hidden-mobile column pt-3 has-text-text is-size-7 has-text-right">{~T:PRSP-Dashboard-Pagination-Template-Description~}</div>
				</header>
				<div class="is-hidden-desktop is-hidden-tablet pb-2 has-text-text is-size-7 has-text-centered">{~T:PRSP-Dashboard-Pagination-Template-Description~}</div>
				<p class="hl-view-description">{~D:Record.RecordSetConfiguration.RecordSetHeaderDescription~}</p>
			<!--  END ImprovedTabularRecordSet Custom: [PRSP-Dashboard-Title-Template] -->
		`);

		this.pict.TemplateProvider.addTemplate('PRSP-Dashboard-RecordList-Template-Table', /*html*/`
			<!-- START ImprovedTabularRecordSet Custom: [PRSP-Dashboard-RecordList-Template-Table] -->
			<div class="table-container">
				<table id="PRSP_List_Table" class="table is-striped is-hoverable is-fullwidth is-bordered">
					<thead>{~T:PRSP-Dashboard-RecordListHeader-Template~}</thead>
					<tbody id="PRSP_RecordList_Container_Entries">
						{~T:PRSP-Dashboard-RecordListEntry-Template~}
						{~T:PRSP-Dashboard-RecordList-Template-EmptyTable~}
					</tbody>
				</table>
			</div>
			<!-- END ImprovedTabularRecordSet Custom: [PRSP-Dashboard-RecordList-Template-Table] -->
		`);
		this.pict.TemplateProvider.addTemplate('PRSP-Dashboard-RecordList-Template-EmptyTable', /*html*/`
			<!-- START ImprovedTabularRecordSet Custom: [PRSP-Dashboard-RecordList-Template-EmptyTable] -->
			<tr id="PRSP_List_Table_Empty">
				<td colspan="100%" class="has-text-centered has-text-text-50">
					<span class="icon">
						<i class="fas fa-info-circle"></i>
					</span>
					<span class="icon-text">No records found.</span>
				</td>
			</tr>
			<!-- END ImprovedTabularRecordSet Custom: [PRSP-Dashboard-RecordList-Template-EmptyTable] -->
		`);
		this.pict.TemplateProvider.addTemplate('PRSP-Dashboard-RecordListEntry-Template-Row-Cell', /*html*/`<td>{~TBDA:Record.Data.PictDashboard.ValueTemplate~}</td>`);
		this.pict.TemplateProvider.addTemplate('PRSP-Dashboard-RecordListAction-Template-Cell', this.options.disableActions ? '' : /*html*/`
			<!-- START ImprovedTabularRecordSet Custom: [PRSP-Dashboard-RecordListAction-Template-Cell'] -->
			<td class="has-text-centered">
				<ul class="buttons has-addons is-centered">
					{~T:HLPC-List-Print-Button-Gate~}
					{~TSWP:PRSP-Dashboard-RecordListAction-Template-Cell-Datum:Pict.providers.RecordSetLinkManager.linkTemplates:Record~}
					${ isDocumentRecord ? /*html*/`<li>
						<a class="button is-ghost" onclick="_Pict.providers.DocumentProvider.openFormByIDDocument(event, {~D:Record.Data.IDDocument~});" title="{~TBR:Record.Name~}">
							<span class="icon">
								<i class="fas fa-pencil"></i>
							</span>
							<span class="icon-text">Edit</span>
						</a>
					</li>` : ''}
				</ul>
			</td>
			<!-- END ImprovedTabularRecordSet Custom:   [PRSP-Dashboard-RecordListAction-Template-Cell] -->`);
		this.pict.TemplateProvider.addTemplate('PRSP-Dashboard-RecordListAction-Template-Cell-Datum', this.options.disableActions ? '' : /*html*/`
			<!-- START ImprovedTabularRecordSet Custom: [PRSP-Dashboard-RecordListAction-Template-Cell-Datum] -->
			<li>
				<a class="button is-ghost" href="{~TBR:Record.Data.URL~}" title="{~TBR:Record.Data.Name~}">
					<span class="icon">
						<i class="fas fa-search"></i>
					</span>
					<span class="icon-text">View</span>
				</a>
			</li>
			<!-- END ImprovedTabularRecordSet Custom: [PRSP-Dashboard-RecordListAction-Template-Cell-Datum] -->
		`);
		this.pict.TemplateProvider.addTemplate('PRSP-Dashboard-RecordListHeader-Template-Header', /*html*/`<th>{~D:Record.DisplayName~}</th>`);
		this.pict.TemplateProvider.addTemplate('PRSP-Dashboard-RecordListActions-Template-Header', this.options.disableActions ? '' : /*html*/`<th class="has-text-centered">Actions</th>`);
		this.pict.TemplateProvider.addTemplate('PRSP-Dashboard-Pagination-Template-Button-Previous',
			`<a class="pagination-previous" href="{~D:Record.PageLinkBookmarks.PreviousLink.URL~}" aria-label="Previous page">Previous</a>`);
		this.pict.TemplateProvider.addTemplate('PRSP-Dashboard-Pagination-Template-Button-Next',
			`<a class="pagination-next" href="{~D:Record.PageLinkBookmarks.NextLink.URL~}" aria-label="Next page">Next</a>`);
		this.pict.TemplateProvider.addTemplate('PRSP-Dashboard-PaginationTop-Template', /*html*/`
			<!-- START ImprovedTabularRecordSet Custom: [PRSP-Dashboard-PaginationTop-Template] -->
			<nav class="pagination is-small my-3 is-rounded" id="RSP_Upper_Pagination_Container" role="navigation" aria-label="pagination">
				{~TIfAbs:PRSP-Dashboard-Pagination-Template-Button-Previous:Record:Record.PageLinkBookmarks.ShowPreviousLink^TRUE^true~}
				{~TIfAbs:PRSP-Dashboard-Pagination-Template-Button-Next:Record:Record.PageLinkBookmarks.ShowNextLink^TRUE^true~}
				<ul class="pagination-list">{~TS:PRSP-Dashboard-Pagination-Template-Pages:Record.PageLinksLimited~}</ul>
			</nav>
			<!-- END ImprovedTabularRecordSet Custom: [PRSP-Dashboard-PaginationTop-Template] -->
		`);
		this.pict.TemplateProvider.addTemplate('PRSP-Dashboard-Pagination-Template-Pages', /*html*/`
			<!-- START ImprovedTabularRecordSet Custom: [PRSP-Dashboard-Pagination-Template-Pages] -->
			<li><a class="pagination-link page-offset_{~D:Record.RelativeOffset~} {~TIfAbs:PaginationActiveClass:Record:Record.RelativeOffset^==^0~}" href="{~D:Record.URL~}" aria-label="Go to page {~D:Record.Page~}">{~D:Record.Page~}</a></li>
			<!-- END ImprovedTabularRecordSet Custom: [PRSP-Dashboard-Pagination-Template-Pages] -->
		`);
		this.pict.TemplateProvider.addTemplate('PaginationActiveClass', /*html*/`is-current`);
		this.pict.TemplateProvider.addTemplate('PRSP-Dashboard-PaginationBottom-Template', `{~T:PRSP-Dashboard-PaginationTop-Template~}`);
		this.pict.TemplateProvider.addTemplate('PRSP-SUBSET-Filters-Template-Button-Fieldset', /*html*/`<!-- intentionally blank -->`);
	}

	/**
	 * The list title-bar "New" button handler. Resolves the active record-set from the current
	 * #/PSRS/<RecordSet>/List hash at click time (race-free) and routes to its Create form. One handler
	 * serves every record-set list in every app that mounts this provider.
	 */
	createFromActiveList()
	{
		const tmpHash = (typeof window !== 'undefined' && window.location) ? (window.location.hash || '') : '';
		const tmpMatch = tmpHash.match(/\/PSRS\/([^/?#]+)/);
		if (tmpMatch && tmpMatch[1])
		{
			window.location.hash = `#/PSRS/${tmpMatch[1]}/Create`;
		}
	}

	/**
	 * Permission-expression layer (Phase 2b): after a record read page renders, toggle a body class that
	 * hides the Edit/Save buttons (via CSS) when the current user can't update the active entity. The entity
	 * is read from the live #/PSRS/<Entity>/(View|Edit|Read) hash, so it is always the record on screen.
	 * Fail-open (no service / no entity / not-yet-loaded → editable); the server stays authoritative.
	 */
	applyReadEditGate()
	{
		if (typeof document === 'undefined' || !document.body)
		{
			return;
		}
		let tmpEntity = '';
		const tmpHash = (typeof window !== 'undefined' && window.location) ? (window.location.hash || '') : '';
		const tmpMatch = /#?\/PSRS\/([A-Za-z0-9_]+)\/(View|Edit|Read)/.exec(tmpHash);
		if (tmpMatch)
		{
			tmpEntity = tmpMatch[1];
		}
		const tmpPermissions = this.pict.fable.EffectivePermissionsService;
		const tmpCanUpdate = (!tmpPermissions || !tmpEntity) ? true : tmpPermissions.can(tmpEntity, 'Update');
		document.body.classList.toggle('pcfg-cannot-update-record', !tmpCanUpdate);
	}
}

module.exports = ImprovedTabularRecordSetProvider;
module.exports.default_configuration = {};
